import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Params } from '@angular/router';
import { params } from '@app/configurations';
import { FadeAnimation } from '@app/core/animations/fade.transition';
import {
  Account,
  GlobalFilterType,
  REFINER_ACCOUNT,
  REFINER_ADGROUP,
  REFINER_CAMPAIGN,
  REFINER_CRITERIA,
  REFINER_DATE,
  REFINER_PG,
  REFINER_SEARCH,
} from '@app/core/models/common.models';
import { StateService } from '@app/core/services/state.service';
import { FindAccount } from '@app/core/store/states/accounts/accounts-state.models';
import { AccountsState } from '@app/core/store/states/accounts/accounts.state';
import {
  GlobalFilterRemoved,
  GlobalFilterSelected,
} from '@app/core/store/states/global-filters/global-filters-state.actions';
import { GlobalFiltersState } from '@app/core/store/states/global-filters/global-filters.state';
import { GoTo } from '@app/core/store/states/router-nav/router-nav-state.actions';
import { RouterPath } from '@app/core/store/states/router-nav/router-nav-state.model';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import {
  FormatDateForURLParam,
  getDateRangeLast30Days,
  getPrevMonthToDate,
  SplitDatesFromUrl,
} from '@app/core/utilities/date.utilities';
import { KeysChanges } from '@app/core/utilities/object.utilities';
import { Primitive } from '@app/core/utilities/typescript.utilities';
import { FilterMode, GlobalSelector } from '@app/shared/modules/global-filters/models/selector.model';
import { TranslateService } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { select, Store } from '@ngxs/store';
import { addDays, startOfDay, startOfMonth } from 'date-fns';
import endOfDay from 'date-fns/endOfDay';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, pairwise } from 'rxjs/operators';
import {
  GetDateRangeTillYesterday,
  GetPreviousDateRange,
} from '../../modules/date-range-selector/date-range.utilities';

export type ForcedFilter =
  | Record<string, Primitive | Primitive[]>
  | { source?: Record<string, Primitive | Primitive[]>; destination?: Record<string, Primitive | Primitive[]> };

export interface Options {
  xAccount?: boolean;
  hasCompareSelector?: boolean;
  hasDelaySelector?: boolean;
  hideInactiveGroup?: boolean;
  disable?: string[];
  disableReset?: string[];
  searchBoxPlaceholder?: string;
  account?: Account;
  enableAdvancedMode?: (
    | typeof REFINER_ACCOUNT
    | typeof REFINER_PG
    | typeof REFINER_CAMPAIGN
    | typeof REFINER_ADGROUP
    | typeof REFINER_CRITERIA
  )[];
  enableAdvancedFilterSelection?: (
    | typeof REFINER_ACCOUNT
    | typeof REFINER_PG
    | typeof REFINER_CAMPAIGN
    | typeof REFINER_ADGROUP
    | typeof REFINER_CRITERIA
  )[];
  destinationSelectors?: {
    [REFINER_PG]?: boolean;
    [REFINER_ADGROUP]?: boolean;
    [REFINER_CAMPAIGN]?: boolean;
  };
  forcedFilters?: {
    [REFINER_ACCOUNT]?: ForcedFilter;
    [REFINER_PG]?: ForcedFilter;
    [REFINER_CAMPAIGN]?: ForcedFilter;
    [REFINER_ADGROUP]?: ForcedFilter;
    [REFINER_CRITERIA]?: ForcedFilter;
  };
}

const Reset = {
  Accounts: { [params.ADSPERT_ID]: null, [params.GLOBAL_FILTER_ACCOUNT_QUERY]: null },
  PGs: { [params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID]: null, [params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY]: null },
  Campaigns: { [params.GLOBAL_FILTER_CAMPAIGN_ID]: null, [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: null },
  AdGroups: { [params.GLOBAL_FILTER_AD_GROUPS_ID]: null, [params.GLOBAL_FILTER_AD_GROUPS_QUERY]: null },
  Criteria: { [params.GLOBAL_FILTER_CRITERIA_ID]: null, [params.GLOBAL_FILTER_CRITERIA_QUERY]: null },
  DestPGs: {
    [params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID]: null,
    [params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY]: null,
  },
  DestCampaigns: { [params.GLOBAL_FILTER_DEST_CAMPAIGN_ID]: null, [params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY]: null },
  DestAdGroups: { [params.GLOBAL_FILTER_DEST_AD_GROUPS_ID]: null, [params.GLOBAL_FILTER_DEST_AD_GROUPS_QUERY]: null },
};

@Component({
  standalone: false,
  selector: 'adspt-global-refiners',
  templateUrl: './global-refiners.component.html',
  styleUrls: ['./global-refiners.component.scss'],
  animations: [FadeAnimation],
})
export class GlobalRefinersComponent implements OnInit, OnChanges, OnDestroy {
  protected readonly REFINER_ACCOUNT = REFINER_ACCOUNT;
  protected readonly REFINER_PG = REFINER_PG;
  protected readonly REFINER_CAMPAIGN = REFINER_CAMPAIGN;
  protected readonly REFINER_ADGROUP = REFINER_ADGROUP;
  protected readonly REFINER_CRITERIA = REFINER_CRITERIA;

  @Input() sticky?: any = null;
  @Input() refiners: any;
  @Input() options: Options = {} as any;
  @Input() disableAdgroups = false;
  @Input() disableCriteria = false;
  @Input() defaultQuery: Record<string, string | null> = {};

  @Output() isPositionStickyActive = new EventEmitter();
  @Output() intialized = new EventEmitter();
  @Output()
  readonly selectionAdded = new EventEmitter<string>();

  @Output()
  readonly selectionRemoved = new EventEmitter<string>();
  @Output()
  readonly changed = new EventEmitter<string>();

  readonly globalFilterNotFoundError$: Observable<GlobalFilterType> = this.store.select(
    GlobalFiltersState.notFoundError,
  );

  readonly delay = select(RouterNavState.hasDelay);
  translations = this.translate.instant('global-filter');
  isOpenInMobile = false;
  isInvalid = false;
  handler;
  queryInput: FormControl = null;
  enableSearchBox = false;
  paramChange$: Subscription;

  @Dispatch()
  readonly delayChange = (delay: boolean) =>
    new GoTo(RouterPath._, { queryParams: { [params.DELAY]: delay ? null : 'false' } });

  dateSelector = {
    value: [],
    compareValue: [],
    render: false,
    enable: false,
    onChange: (data: any) => {
      try {
        const [current, compare] = data;
        const tmp = {
          [params.GLOBAL_FILTER_DATE_RANGE]: current.map((d) => FormatDateForURLParam(d)).join(','),
        };
        if (this.options.hasCompareSelector) {
          tmp[params.GLOBAL_FILTER_DATE_COMPARE] = compare.map((d) => FormatDateForURLParam(d)).join(',');
        }
        this.stateService.changeState(tmp);
      } catch (error) {
        console.log(data, error);
      }
    },
  };
  accountSelector = {
    enableAdvancedMode: false,
    enableAdvancedFilterSelection: false,
    disableReset: false,
    render: false,
    enable: false,
    disable: false,
    onSelectionChange: (data) => {
      // flush child selections
      const state = this.stateService.getState();
      const campaignQuery = this._campaignQuery(state);
      if (data.type === 'FILTER') {
        this.stateService.changeState({
          ...Reset.Accounts,
          ...Reset.PGs,
          ...Reset.Campaigns,
          ...Reset.AdGroups,
          ...Reset.Criteria,
          ...Reset.DestPGs,
          ...Reset.DestCampaigns,
          ...Reset.DestAdGroups,
          [params.GLOBAL_FILTER_ACCOUNT_QUERY]: data.payload === '' ? null : data.payload,
          [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: campaignQuery,
        });
        this._onSelectionRemoved(REFINER_ACCOUNT);
      } else {
        const dates = this._checkIfDateRangeIsAdpsertStart(data.payload.id);
        this.stateService.changeState({
          ...Reset.Accounts,
          ...Reset.PGs,
          ...Reset.Campaigns,
          ...Reset.AdGroups,
          ...Reset.Criteria,
          ...Reset.DestPGs,
          ...Reset.DestCampaigns,
          ...Reset.DestAdGroups,
          [params.GLOBAL_FILTER_DATE_RANGE]: dates?.dateRange || null,
          [params.GLOBAL_FILTER_DATE_COMPARE]: dates?.compareDateRange || null,
          [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: campaignQuery,
          [params.ADSPERT_ID]: data.payload.id,
        });

        this._onSelectionAdded(REFINER_ACCOUNT);
      }
    },
    onReset: () => {
      const state = this.stateService.getState();
      const campaignQuery = this._campaignQuery(state);
      this.stateService.changeState({
        ...Reset.Accounts,
        ...Reset.PGs,
        ...Reset.Campaigns,
        ...Reset.AdGroups,
        ...Reset.Criteria,
        ...Reset.DestPGs,
        ...Reset.DestCampaigns,
        ...Reset.DestAdGroups,
        [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: campaignQuery,
      });

      this._onSelectionRemoved(REFINER_ACCOUNT);
    },
  };
  pgSelector: GlobalSelector = {
    render: false,
    enable: false,
    enableAdvancedMode: false,
    enableAdvancedFilterSelection: false,
    disable: false,
    disableReset: false,
    notSelectable: false,
    onSelectionChange: (data) => {
      const state = this.stateService.getState();
      if (data.tab === FilterMode.Source) {
        const campaignQuery = this._campaignQuery(state);
        if (data.type === 'FILTER') {
          this.stateService.changeState({
            ...Reset.PGs,
            ...Reset.Campaigns,
            ...Reset.AdGroups,
            ...Reset.Criteria,
            [params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY]: data.payload === '' ? null : data.payload,
            [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: campaignQuery,
          });

          this._onSelectionRemoved(REFINER_PG);
        } else {
          this.stateService.changeState({
            ...Reset.PGs,
            ...Reset.Campaigns,
            ...Reset.AdGroups,
            ...Reset.Criteria,
            [params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID]: data.payload.id,
            [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: campaignQuery,
          });

          this._onSelectionAdded(REFINER_PG);
        }
      } else {
        const campaignQuery =
          state[params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY] === '' ? '' : (this.defaultQuery?.[REFINER_CAMPAIGN] ?? null);
        if (data.type === 'FILTER') {
          this.stateService.changeState({
            ...Reset.DestPGs,
            ...Reset.DestCampaigns,
            ...Reset.DestAdGroups,
            [params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY]: data.payload === '' ? null : data.payload,
            [params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY]: campaignQuery,
          });

          this._onSelectionRemoved(REFINER_PG, true);
        } else {
          this.stateService.changeState({
            ...Reset.DestPGs,
            ...Reset.DestCampaigns,
            ...Reset.DestAdGroups,
            [params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID]: data.payload.id,
            [params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY]: campaignQuery,
          });

          this._onSelectionAdded(REFINER_PG, true);
        }
      }
    },
    onReset: (data) => {
      let refiners = {};
      if (data?.tab === FilterMode.Source) refiners = Reset.PGs;
      else if (data?.tab === FilterMode.Destination) refiners = Reset.DestPGs;
      else refiners = { ...Reset.PGs, ...Reset.DestPGs };

      this.stateService.changeState(refiners);
      this._onSelectionRemoved(REFINER_PG, data?.tab === FilterMode.Destination);
    },
  };
  campaignSelector: GlobalSelector = {
    render: false,
    enable: false,
    enableAdvancedMode: false,
    enableAdvancedFilterSelection: false,
    disable: false,
    disableReset: false,
    onSelectionChange: (data) => {
      if (data.tab === FilterMode.Source) {
        if (data.type === 'FILTER') {
          this.stateService.changeState({
            ...Reset.Campaigns,
            ...Reset.AdGroups,
            ...Reset.Criteria,
            [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: data.payload,
          });

          this._onSelectionRemoved(REFINER_CAMPAIGN);
        } else {
          this.stateService.changeState({
            ...Reset.Campaigns,
            ...Reset.AdGroups,
            ...Reset.Criteria,
            [params.GLOBAL_FILTER_CAMPAIGN_ID]: data.payload.id,
          });

          this._onSelectionAdded(REFINER_CAMPAIGN);
        }
      } else {
        if (data.type === 'FILTER') {
          this.stateService.changeState({
            ...Reset.DestCampaigns,
            ...Reset.DestAdGroups,
            [params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY]: data.payload,
          });

          this._onSelectionRemoved(REFINER_CAMPAIGN, true);
        } else {
          this.stateService.changeState({
            ...Reset.DestCampaigns,
            ...Reset.DestAdGroups,
            [params.GLOBAL_FILTER_DEST_CAMPAIGN_ID]: data.payload.id,
          });

          this._onSelectionAdded(REFINER_CAMPAIGN, true);
        }
      }
    },
    onReset: (data) => {
      let refiners = {};
      if (data?.tab === FilterMode.Source) {
        refiners = {
          ...Reset.Campaigns,
          ...Reset.AdGroups,
          ...Reset.Criteria,
          [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: this.defaultQuery?.[REFINER_CAMPAIGN] ?? null,
        };
      } else if (data?.tab === FilterMode.Destination) {
        refiners = {
          ...Reset.DestCampaigns,
          ...Reset.DestAdGroups,
          [params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY]: this.defaultQuery?.[REFINER_CAMPAIGN] ?? null,
        };
      } else {
        refiners = {
          ...Reset.Campaigns,
          ...Reset.AdGroups,
          ...Reset.Criteria,
          ...Reset.DestCampaigns,
          ...Reset.DestAdGroups,
        };
      }

      this.stateService.changeState(refiners);
      this._onSelectionRemoved(REFINER_CAMPAIGN, data?.tab === FilterMode.Destination);
    },
  };
  adGroupsSelector: GlobalSelector = {
    render: false,
    enable: false,
    enableAdvancedMode: false,
    enableAdvancedFilterSelection: false,
    disable: false,
    disableReset: false,
    onSelectionChange: (data) => {
      if (data.tab === FilterMode.Source) {
        if (data.type === 'FILTER') {
          this.stateService.changeState({
            ...Reset.AdGroups,
            ...Reset.Criteria,
            [params.GLOBAL_FILTER_AD_GROUPS_QUERY]: data.payload,
          });

          this._onSelectionRemoved(REFINER_ADGROUP);
        } else {
          this.stateService.changeState({
            ...Reset.AdGroups,
            ...Reset.Criteria,
            [params.GLOBAL_FILTER_AD_GROUPS_ID]: data.payload.id,
          });

          this._onSelectionAdded(REFINER_ADGROUP);
        }
      } else {
        if (data.type === 'FILTER') {
          this.stateService.changeState({
            ...Reset.AdGroups,
            [params.GLOBAL_FILTER_DEST_AD_GROUPS_QUERY]: data.payload,
          });

          this._onSelectionRemoved(REFINER_ADGROUP, true);
        } else {
          this.stateService.changeState({
            ...Reset.DestAdGroups,
            [params.GLOBAL_FILTER_DEST_AD_GROUPS_ID]: data.payload.id,
          });

          this._onSelectionAdded(REFINER_ADGROUP, true);
        }
      }
    },
    onReset: (data) => {
      let refiners = {};
      if (data?.tab === FilterMode.Source) {
        refiners = {
          ...Reset.AdGroups,
          ...Reset.Criteria,
        };
      } else if (data?.tab === FilterMode.Destination) {
        refiners = {
          ...Reset.DestAdGroups,
        };
      } else {
        refiners = {
          ...Reset.AdGroups,
          ...Reset.Criteria,
          ...Reset.DestAdGroups,
        };
      }

      this.stateService.changeState(refiners);
      this._onSelectionRemoved(REFINER_ADGROUP, data?.tab === FilterMode.Destination);
    },
  };
  criteriaSelector: GlobalSelector = {
    render: false,
    enable: false,
    enableAdvancedMode: false,
    enableAdvancedFilterSelection: false,
    disable: false,
    disableReset: false,
    onSelectionChange: (data) => {
      if (data.type === 'FILTER') {
        this.stateService.changeState({
          ...Reset.Criteria,
          [params.GLOBAL_FILTER_CRITERIA_QUERY]: data.payload,
        });

        this._onSelectionRemoved(REFINER_CRITERIA);
      } else {
        this.stateService.changeState({
          ...Reset.Criteria,
          [params.GLOBAL_FILTER_CRITERIA_ID]: `${data.payload.adspertAdgroupId}:${data.payload.id}`,
        });

        this._onSelectionAdded(REFINER_CRITERIA);
      }
    },
    onReset: () => {
      this.stateService.changeState(Reset.Criteria);

      this._onSelectionRemoved(REFINER_CRITERIA);
    },
  };

  onSearchReset() {
    // flush search input and update the state
    this.queryInput.setValue(null);
    this.stateService.changeState({ [params.GLOBAL_FILTER_SEARCH_QUERY]: null });
  }

  private _onSelectionAdded(queryParam: string, isDestination?: boolean) {
    this.store.dispatch(new GlobalFilterSelected(queryParam, isDestination));
    this.onSelectionAdded(queryParam, isDestination);
    setTimeout(() => {
      this.changed.emit();
      this.cd.detectChanges();
    });
  }
  private _onSelectionRemoved(queryParam: string, isDestination?: boolean) {
    this.store.dispatch(new GlobalFilterRemoved(queryParam, isDestination));
    this.onSelectionRemoved(queryParam, isDestination);
    setTimeout(() => {
      this.changed.emit();
      this.cd.detectChanges();
    });
  }

  private _campaignQuery(state: {}) {
    return state[params.GLOBAL_FILTER_CAMPAIGN_QUERY] === '' ? '' : (this.defaultQuery?.[REFINER_CAMPAIGN] ?? null);
  }
  private _setSearchQueryValue(value: string) {
    this.queryInput.setValue(value, { emitEvent: false });
    this.changed.emit();
    this.cd.detectChanges();
  }

  private _setDateRangeValue(state: Params): { default: any; compare: any } {
    const ranges = {
      default: state[params.GLOBAL_FILTER_DATE_RANGE] || null,
      compare: state[params.GLOBAL_FILTER_DATE_COMPARE] || null,
    };

    if (!ranges.default) {
      const t = getDateRangeLast30Days(false)
        .map((d) => FormatDateForURLParam(d))
        .join(',');
      ranges.default = t;
    }

    this.dateSelector.value = SplitDatesFromUrl(ranges.default);
    this.dateSelector.value[1] = endOfDay(this.dateSelector.value[1]);

    if (this.options.hasCompareSelector) {
      if (!ranges.compare) {
        const thisMonth = [startOfMonth(startOfDay(new Date())), addDays(endOfDay(new Date()), -1)];
        if (+thisMonth[0] === +this.dateSelector.value[0] && +thisMonth[1] === +this.dateSelector.value[1])
          ranges.compare = getPrevMonthToDate(<any>this.dateSelector.value);
        else ranges.compare = GetPreviousDateRange(<[Date, Date]>this.dateSelector.value);

        ranges.compare = ranges.compare.map((d) => FormatDateForURLParam(d)).join(',');
      }

      this.dateSelector.compareValue = SplitDatesFromUrl(ranges.compare);
      this.dateSelector.compareValue[1] = endOfDay(this.dateSelector.compareValue[1]);
    }

    this.changed.emit();
    return ranges;
  }

  private _validateAndRenderAccountsSelector() {
    this.accountSelector.render = true;
  }
  private _validateAndRenderPgsSelector(state: {}) {
    // group selector is dependent accounts selector, enabled only when account filter is selected
    // it is also notSelectable when campaign is selected directly
    if (this.options.xAccount) {
      this.pgSelector.notSelectable = false;
    } else {
      this.pgSelector.notSelectable =
        !state[params.ADSPERT_ID] ||
        (!!state[params.GLOBAL_FILTER_CAMPAIGN_ID] && !state[params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID]);
    }
    this.pgSelector.render = true;
  }
  private _validateAndRenderCampaignSelector() {
    this.campaignSelector.render = true;
  }
  private _validateAndRenderAdGroupsSelector() {
    this.adGroupsSelector.render = true;
  }
  private _validateAndRenderCriteriaSelector(state: {}) {
    this.criteriaSelector.notSelectable = !state[params.ADSPERT_ID] && !state[params.GLOBAL_FILTER_AD_GROUPS_ID];
    this.criteriaSelector.render = true;
  }
  private _init(account: Account) {
    const state = this.stateService.getState();
    if (!this.options.xAccount) state[params.ADSPERT_ID] = account.adspertAccountId;

    // validate state and update
    const dates = this._setDateRangeValue(state);
    state[params.GLOBAL_FILTER_DATE_RANGE] = dates.default;
    state[params.GLOBAL_FILTER_DATE_COMPARE] = dates.compare;

    this.dateSelector.render = true;
    // set initial query param values
    this._setSearchQueryValue(state[params.GLOBAL_FILTER_SEARCH_QUERY]);
    this._validateAndRenderAccountsSelector();
    this._validateAndRenderPgsSelector(state);
    this._validateAndRenderCampaignSelector();
    this._validateAndRenderAdGroupsSelector();
    this._validateAndRenderCriteriaSelector(state);
    // update state if changed
    this.stateService.changeState(state, 'merge', true);
    setTimeout(() => this.intialized.emit());
  }
  private _setOptions(
    key: 'disableReset' | 'enableAdvancedMode' | 'enableAdvancedFilterSelection' | 'disable',
    changes?: any,
  ) {
    const newOptions = changes?.options;

    if (
      this.options[key] ||
      (!!newOptions &&
        newOptions?.previousValue &&
        newOptions?.currentValue[key] &&
        newOptions?.currentValue[key] !== newOptions?.previousValue[key])
    ) {
      this.accountSelector[key] = false;
      this.pgSelector[key] = false;
      this.campaignSelector[key] = false;
      this.adGroupsSelector[key] = false;
      this.criteriaSelector[key] = false;
      const tmp = newOptions?.currentValue ?? this.options;

      tmp[key].forEach((e: string) => {
        switch (e) {
          case REFINER_ACCOUNT:
            this.accountSelector[key] = true;
            break;
          case REFINER_PG:
            this.pgSelector[key] = true;
            break;
          case REFINER_CAMPAIGN:
            this.campaignSelector[key] = true;
            break;
          case REFINER_ADGROUP:
            this.adGroupsSelector[key] = true;
            break;
          case REFINER_CRITERIA:
            this.criteriaSelector[key] = true;
            break;
          default:
            break;
        }
      });
    }
  }
  private onSelectionAdded(refiner: string, isDest = false): void {
    setTimeout(() => this.selectionAdded.emit(<any>(isDest ? { isDest, refiner } : refiner)));
  }
  private onSelectionRemoved(refiner: string, isDest = false): void {
    setTimeout(() => this.selectionRemoved.emit(<any>(isDest ? { isDest, refiner } : refiner)));
  }
  private _checkIfDateRangeIsAdpsertStart(newAdspertId) {
    const state = this.stateService.getState();

    const returnValue = {
      dateRange: state[params.GLOBAL_FILTER_DATE_RANGE],
      compareDateRange: state[params.GLOBAL_FILTER_DATE_COMPARE],
    };

    const currentAdspertId = state[params.ADSPERT_ID];

    if (!currentAdspertId) return returnValue;

    const currentAccountRange = GetDateRangeTillYesterday(
      this.store.selectSnapshot(AccountsState.adpsertAccountStartDate),
    )
      .map((d) => FormatDateForURLParam(d || new Date()))
      .join(',');

    if (returnValue.dateRange === currentAccountRange) {
      const tmp = FindAccount(this.store.selectSnapshot(AccountsState.allAccounts), newAdspertId);
      const newDateRange = !tmp ? null : GetDateRangeTillYesterday(new Date(tmp.optimizationStart || tmp.connectedAt));

      const newCompareDateRange = !returnValue.compareDateRange
        ? null
        : GetPreviousDateRange(newDateRange)
            ?.map((d) => FormatDateForURLParam(d))
            ?.join(',');

      return {
        dateRange: newDateRange.map((d) => FormatDateForURLParam(d)).join(','),
        compareDateRange: newCompareDateRange,
      };
    }
    return returnValue;
  }

  constructor(
    private element: ElementRef,
    private translate: TranslateService,
    private stateService: StateService,
    private cd: ChangeDetectorRef,
    private store: Store,
  ) {
    if (window.innerWidth > 768) this.isOpenInMobile = false; // close the filter in large screens

    this.queryInput = new FormControl();
    this.queryInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value) => {
      if (value !== undefined && value !== null) {
        this.stateService.changeState({
          [params.GLOBAL_FILTER_SEARCH_QUERY]: !value ? null : value,
        });
      }
    });

    this.paramChange$ = this.store
      .select(RouterNavState.queryParams)
      .pipe(pairwise())
      .subscribe(([prev, current]) => {
        switch (true) {
          case KeysChanges([params.GLOBAL_FILTER_DATE_RANGE, params.GLOBAL_FILTER_DATE_COMPARE], prev, current):
            this._setDateRangeValue(current);
            break;
          case KeysChanges([params.ADSPERT_ID], prev, current):
            this._validateAndRenderPgsSelector(current);
            this._validateAndRenderAdGroupsSelector();
            this._validateAndRenderCriteriaSelector(current);
            break;
          case KeysChanges(
            [
              params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID,
              params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY,
              params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID,
              params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY,
            ],
            prev,
            current,
          ):
            this._validateAndRenderPgsSelector(current);
            break;
          case KeysChanges([params.GLOBAL_FILTER_CAMPAIGN_ID, params.GLOBAL_FILTER_CAMPAIGN_QUERY], prev, current):
            this._validateAndRenderPgsSelector(current);
            this._validateAndRenderAdGroupsSelector();
            break;
          case KeysChanges([params.GLOBAL_FILTER_AD_GROUPS_ID, params.GLOBAL_FILTER_AD_GROUPS_QUERY], prev, current):
            this._validateAndRenderAdGroupsSelector();
            this._validateAndRenderCriteriaSelector(current);
            break;
          case KeysChanges([params.GLOBAL_FILTER_CRITERIA_ID, params.GLOBAL_FILTER_CRITERIA_QUERY], prev, current):
            this._validateAndRenderCriteriaSelector(current);
            break;
        }
        if (prev[params.GLOBAL_FILTER_SEARCH_QUERY] !== current[params.GLOBAL_FILTER_SEARCH_QUERY]) {
          return this._setSearchQueryValue(current[params.GLOBAL_FILTER_SEARCH_QUERY]);
        }
      });
  }
  ngOnInit() {
    this.refiners.forEach((e: string) => {
      switch (e) {
        case REFINER_ACCOUNT:
          this.accountSelector.enable = true;
          break;
        case REFINER_PG:
          this.pgSelector.enable = true;
          break;
        case REFINER_CAMPAIGN:
          this.campaignSelector.enable = true;
          break;
        case REFINER_ADGROUP:
          this.adGroupsSelector.enable = true;
          break;
        case REFINER_CRITERIA:
          this.criteriaSelector.enable = true;
          break;
        case REFINER_SEARCH:
          this.enableSearchBox = true;
          break;
        case REFINER_DATE:
          this.dateSelector.enable = true;
          break;
        default:
          break;
      }
    });
    this._setOptions('disableReset');
    this._setOptions('enableAdvancedMode');
    this._setOptions('enableAdvancedFilterSelection');
    this._setOptions('disable');

    this.setXAccount();

    if (this.sticky === '' || !!this.sticky) {
      this.element.nativeElement.classList.add('sticky');
      let ticking = false;
      this.handler = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            const posY = this.element.nativeElement.getBoundingClientRect().y;
            if (posY === 65) {
              this.element.nativeElement.classList.add('active');
              this.isPositionStickyActive.emit(true);
            } else if (posY > 65) {
              this.element.nativeElement.classList.remove('active');
              this.isPositionStickyActive.emit(false);
            }
            ticking = false;
          });
          ticking = true;
        }
      };
      window.addEventListener('scroll', this.handler);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this._setOptions('disableReset', changes);
    this._setOptions('enableAdvancedMode', changes);
    this._setOptions('enableAdvancedFilterSelection', changes);
    this._setOptions('disable', changes);
    if (changes.options && this.options?.destinationSelectors !== changes.options.previousValue?.destinationSelectors) {
      const state = this.stateService.getState();
      this.pgSelector.render = false;
      this.campaignSelector.render = false;
      this.adGroupsSelector.render = false;
      setTimeout(() => {
        this._validateAndRenderPgsSelector(state);
        this._validateAndRenderCampaignSelector();
        this._validateAndRenderAdGroupsSelector();
        this.cd.detectChanges();
      });
    }

    if (
      !changes.options.firstChange &&
      !changes.options.currentValue?.xAccount !== !changes.options.previousValue?.xAccount
    ) {
      this.setXAccount();
    }
  }
  ngOnDestroy() {
    // destroy state change subscription
    this.paramChange$?.unsubscribe();

    if (!!this.handler) {
      window.removeEventListener('scroll', this.handler);
    }
  }

  private setXAccount(): void {
    if (this.options.xAccount) {
      // x-account
      this.isInvalid = false;
      this._init(null);
    } else {
      if (!!this.options.account) {
        this.isInvalid = false;
        this._init(this.options.account);
      } else {
        this.isInvalid = true;
        this.intialized.emit(); // { account: this.options.account }
      }
    }
  }
}
